import React, { useState } from 'react';
import emailjs from 'emailjs-com';
import '../style/contactus.css';
import { FaUser, FaEnvelope, FaPhone, FaComment, FaPaperPlane, FaCheck, FaExclamationTriangle } from 'react-icons/fa';

const ContactUs = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    contact: '',
    subject: '',
    message: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // EmailJS configuration
  const EMAILJS_SERVICE_ID = 'service_0czt78n';
  const EMAILJS_TEMPLATE_ID = 'template_ctucoh7';
  const EMAILJS_USER_ID = '0DOhXQiwvlHU5vq21';

  const validateForm = () => {
    const newErrors = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Contact validation
    if (!formData.contact.trim()) {
      newErrors.contact = 'Phone number is required';
    } else if (!/^\+?[\d\s-()]{10,}$/.test(formData.contact)) {
      newErrors.contact = 'Please enter a valid phone number';
    }

    // Subject validation
    if (!formData.subject) {
      newErrors.subject = 'Please select a subject';
    }

    // Message validation
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Send email using EmailJS - Updated to use company email
      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        contact_number: formData.contact,
        subject: formData.subject,
        message: formData.message,
        to_email: 'info@404fxvisuals.com', // Changed to company email
        reply_to: formData.email
      };

      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams,
        EMAILJS_USER_ID
      );

      // Reset form on success
      setFormData({
        name: '',
        email: '',
        contact: '',
        subject: '',
        message: ''
      });
      
      setIsSubmitted(true);
      setErrors({});

      // Hide success message after 5 seconds
      setTimeout(() => {
        setIsSubmitted(false);
      }, 5000);

    } catch (error) {
      console.error('Error sending email:', error);
      setErrors({ submit: 'Failed to send message. Please try again or email us directly at info@404fxvisuals.com' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getInputClassName = (fieldName) => {
    return `form-input ${errors[fieldName] ? 'error' : ''} ${
      formData[fieldName] && !errors[fieldName] ? 'success' : ''
    }`;
  };

  return (
    <div className="contact-container">
      <div className="contact-content">
        {/* Left Text Content */}
        <div className="contact-text">
          <h1 className="contact-heading">
            Tired of boring visuals?
          </h1>
          <p className="contact-subheading">
            Launch your viral content with us.
          </p>
          <div className="brand-highlight">
            <span className="brand-number">404</span>
            <span className="brand-name">FXVISUALS</span>
          </div>
        </div>

        {/* Right Form Content */}
        <div className="contact-form-container">
          <form className="contact-form" onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <div className="input-container">
                  <FaUser className="input-icon" />
                  <input
                    type="text"
                    name="name"
                    placeholder="Your Name"
                    value={formData.name}
                    onChange={handleChange}
                    className={getInputClassName('name')}
                    required
                  />
                </div>
                {errors.name && <span className="error-message">{errors.name}</span>}
              </div>

              <div className="form-group">
                <div className="input-container">
                  <FaEnvelope className="input-icon" />
                  <input
                    type="email"
                    name="email"
                    placeholder="Email Address"
                    value={formData.email}
                    onChange={handleChange}
                    className={getInputClassName('email')}
                    required
                  />
                </div>
                {errors.email && <span className="error-message">{errors.email}</span>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <div className="input-container">
                  <FaPhone className="input-icon" />
                  <input
                    type="tel"
                    name="contact"
                    placeholder="Phone Number"
                    value={formData.contact}
                    onChange={handleChange}
                    className={getInputClassName('contact')}
                    required
                  />
                </div>
                {errors.contact && <span className="error-message">{errors.contact}</span>}
              </div>
              
              <div className="form-group">
                <div className="input-container select-container">
                  <select 
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    className={`form-input select-input ${getInputClassName('subject')}`}
                    required
                  >
                    <option value="">Select Subject</option>
                    <option value="Video Production">Video Production</option>
                    <option value="Motion Graphics">Motion Graphics</option>
                    <option value="Visual Effects">Visual Effects</option>
                    <option value="3D Animation">3D Animation</option>
                    <option value="Consultation">Consultation</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                {errors.subject && <span className="error-message">{errors.subject}</span>}
              </div>
            </div>

            <div className="form-group">
              <div className="input-container">
                <FaComment className="input-icon message-icon" />
                <textarea
                  name="message"
                  placeholder="Your Message"
                  value={formData.message}
                  onChange={handleChange}
                  className={`form-input textarea-input ${getInputClassName('message')}`}
                  rows="4"
                  required
                ></textarea>
              </div>
              {errors.message && <span className="error-message">{errors.message}</span>}
            </div>

            {errors.submit && (
              <div className="error-message" style={{textAlign: 'center', marginBottom: '1rem'}}>
                <FaExclamationTriangle style={{marginRight: '0.5rem'}} />
                {errors.submit}
              </div>
            )}

            {isSubmitted && (
              <div className="success-message">
                <FaCheck style={{marginRight: '0.5rem'}} />
                Message sent successfully! We'll get back to you soon.
              </div>
            )}

            <button 
              type="submit" 
              className="submit-btn"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <div className="loading"></div>
                  Sending...
                </>
              ) : (
                <>
                  <FaPaperPlane className="btn-icon" />
                  Send Message
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ContactUs;